/**
 * Core domain types. Pure data — no React, no Dexie, no browser APIs.
 *
 * Dates are always 'YYYY-MM-DD' strings, never Date objects: Date serializes
 * inconsistently and drags timezone bugs into a dataset where every value is a
 * calendar day in Israel.
 */

export type IssuerId =
  | 'isracard'
  | 'cal'
  | 'max'
  | 'amex-il'
  | 'hapoalim'
  | 'bank-of-cyprus'
  | 'manual';

export const ISSUER_LABELS: Record<IssuerId, string> = {
  isracard: 'Isracard',
  cal: 'Cal (Visa)',
  max: 'Max',
  'amex-il': 'American Express IL',
  hapoalim: 'Bank Hapoalim',
  'bank-of-cyprus': 'Bank of Cyprus',
  manual: 'Manually mapped',
};

/** ISO-4217 code, or the raw symbol when we could not resolve one. */
export type CurrencyCode = string;

/** Where this payment source is used for the Israel/abroad spending split. */
export type CardRegion = 'israel' | 'abroad';

/** Optional only for legacy records; repository writes stamp modifications. */
export interface ConfigRecord {
  updatedAt?: string;
}

export interface Person extends ConfigRecord {
  id: string;
  displayName: string;
  /**
   * Cardholder names exactly as they appear on statements ("על שם ..."). Used to
   * attribute an uploaded file to a person without asking.
   */
  statementAliases: string[];
  color: string;
}

export interface Card extends ConfigRecord {
  id: string;
  personId: string;
  issuer: IssuerId;
  /** Last 4 digits as printed on the statement. */
  last4: string;
  nickname: string;
  /** Issuer's product name, e.g. "קורפוריט - זהב". */
  productName: string | null;
  /** A card or bank account can be classified independently for reporting. */
  region: CardRegion;
}

export interface InstallmentInfo {
  /** 1-based index of this charge, e.g. 2 in "תשלום 2 מתוך 3". */
  current: number;
  total: number;
  /** Full purchase price, not the monthly charge. */
  totalAmount: number;
}

/**
 * `sourceFileId` for a row typed in by hand rather than imported.
 *
 * A sentinel rather than null, because `sourceFileId` is what an undo groups
 * on: a real statement id can never equal this, so undoing an import can never
 * sweep up hand-entered rows, and they simply never appear in the import
 * history.
 */
export const MANUAL_SOURCE_FILE_ID = 'manual';

export interface Transaction {
  id: string;
  /** Human-readable key behind `id`. Kept so dedupe decisions are debuggable. */
  dedupeKey: string;

  personId: string;
  cardId: string;

  /** When the purchase happened. */
  transactionDate: string;
  /** When the money actually leaves the account. Statement-level on Isracard. */
  chargeDate: string | null;

  merchantRaw: string;
  merchantNormalized: string;

  /** Billed amount for this cycle, in ILS. Negative means a refund/credit. */
  amountILS: number;
  /** Foreign transactions only: amount in the currency actually charged. */
  originalAmount: number | null;
  originalCurrency: CurrencyCode | null;

  categoryId: string | null;
  subcategoryId: string | null;

  notes: string | null;
  tags: string[];

  installment: InstallmentInfo | null;
  isRefund: boolean;
  /** הוראת קבע — a standing order. Useful for recurring-charge detection. */
  isStandingOrder: boolean;
  /** Excluded from every chart and total, but still visible in the table. */
  excludedFromReports: boolean;

  /**
   * Issuer's own transaction reference (Isracard: מס' שובר). When present this
   * is the dedupe identity — far more reliable than hashing the visible fields.
   */
  reference: string | null;
  /** Raw issuer annotation column, kept verbatim for auditing. */
  extraDetails: string | null;

  sourceFileId: string;
  importedAt: string;
  /**
   * Last local modification. This is what makes a two-device merge decidable:
   * when both sides hold the same transaction, the later `updatedAt` wins.
   * Without it there is no principled way to reconcile a row categorized on
   * one device and tagged on another.
   */
  updatedAt: string;
  /** Set once a human touches the row; blocks destructive undo. */
  manuallyEdited: boolean;
}

export interface Statement {
  id: string;
  filename: string;
  /** SHA-256 of the file bytes — detects re-uploading the exact same export. */
  fileHash: string;
  issuer: IssuerId;
  cardId: string;
  personId: string;
  periodFrom: string | null;
  periodTo: string | null;
  chargeDate: string | null;
  rowCount: number;
  importedCount: number;
  duplicateCount: number;
  reviewCount: number;
  unparsedCount: number;
  importedAt: string;
  /** Issuer's stated total, when the file carries one. Reconciliation check. */
  statedTotal: number | null;
}

export type CategoryKind = 'fixed' | 'variable';
export type CategoryNeed = 'essential' | 'discretionary';

export interface Category extends ConfigRecord {
  id: string;
  parentId: string | null;
  labelHe: string;
  labelEn: string;
  color: string;
  kind: CategoryKind;
  need: CategoryNeed;
  order: number;
  /** Built-in categories can be renamed but not deleted. */
  builtin: boolean;
}

export interface RuleMatch {
  merchant?: { mode: 'substring' | 'regex'; value: string; caseSensitive?: boolean };
  amount?: { min?: number; max?: number };
  cardId?: string;
  currency?: CurrencyCode;
  /** Matches against the issuer's extra-details column. */
  detailsContains?: string;
}

export interface Rule extends ConfigRecord {
  id: string;
  order: number;
  enabled: boolean;
  name: string;
  match: RuleMatch;
  assign: {
    categoryId: string;
    subcategoryId?: string;
    tags?: string[];
  };
}

export interface Budget extends ConfigRecord {
  id: string;
  categoryId: string;
  /** ILS per month. */
  monthlyAmount: number;
  personId: string | null;
}

/** How amounts are projected into reports. See domain/installments.ts. */
export type AmountBasis = 'cashflow' | 'purchase';
/** Which date buckets a transaction into a month. */
export type DateBasis = 'transaction' | 'charge';
export type RefundMode = 'net' | 'exclude' | 'separate';

export interface Settings extends ConfigRecord {
  amountBasis: AmountBasis;
  dateBasis: DateBasis;
  refundMode: RefundMode;
  locale: 'he-IL';
}

export const DEFAULT_SETTINGS: Settings = {
  amountBasis: 'cashflow',
  dateBasis: 'transaction',
  refundMode: 'net',
  locale: 'he-IL',
};

/**
 * A record that a row was deleted, so the deletion survives a merge.
 *
 * Sync merges by union, and a union cannot express absence: a row the local
 * copy lacks is indistinguishable from one it has never seen, so the remote
 * copy re-adds it on the next sync. A tombstone is the missing signal.
 *
 * Kept per collection rather than in one global id space, so a stray id
 * collision between, say, a rule and a transaction cannot delete the wrong row.
 */
export type TombstoneCollection =
  | 'people'
  | 'cards'
  | 'categories'
  | 'rules'
  | 'transactions'
  | 'statements';

export interface Tombstone {
  collection: TombstoneCollection;
  id: string;
  /** ISO timestamp. Also what garbage collection ages out. */
  deletedAt: string;
}

/**
 * How long a tombstone is honoured. A device that has been offline longer than
 * this can still resurrect what it deleted — the alternative is keeping every
 * deletion on record forever, which grows the synced document without bound.
 */
export const TOMBSTONE_MAX_AGE_DAYS = 90;
